/**
 * Página de Información de Ayudas y Subvenciones - Portal de Transparencia
 * Estilo Awwwards Interior - Información sobre ayudas y subvenciones recibidas
 */

import React, { useRef, useEffect, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import LocalizedLink from '../../components/common/LocalizedLink';
import { useTranslation, Trans } from 'react-i18next';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ImmersiveLayout from '../../components/layout/ImmersiveLayout';
import { AnimatedSection } from '../../components/animations';

gsap.registerPlugin(ScrollTrigger);

/**
 * Página de Información de Ayudas y Subvenciones con estilo Awwwards
 * @returns {JSX.Element} Página de información de ayudas y subvenciones
 */
const SubsidiesInfoPage = () => {
  const { t } = useTranslation(['transparencySubsidies', 'transparency']);
  const heroRef = useRef(null);
  const location = useLocation();

  /**
   * Efecto para hacer scroll al inicio de la página al cargar
   */
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    }
    
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
      if (window.__lenis) {
        window.__lenis.scrollTo(0, { immediate: true });
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [location.key]);

  // Animación del hero al cargar
  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const tl = gsap.timeline();
    tl.fromTo(hero.querySelector('.hero-title'),
      { y: 50, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }
    );

    return () => tl.kill();
  }, []);

  return (
    <ImmersiveLayout>
      {/* Hero Section - Estilo Portal Transparencia */}
      <section 
        ref={heroRef}
        className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden"
      >
        {/* Fondo negro base */}
        <div className="absolute inset-0 bg-intercasa-dark" />
        
        {/* Imagen en patrón repetido (tile) */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(/images/banners/banner-negro.webp)',
            backgroundSize: '350px auto',
            backgroundRepeat: 'repeat',
            backgroundPosition: 'center',
          }}
        />
        
        {/* Gradiente para mejor legibilidad */}
        <div className="absolute inset-0 bg-gradient-to-r from-intercasa-dark/90 via-intercasa-dark/70 to-intercasa-dark/90" />
        
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-4xl">
            {/* Breadcrumb */}
            <LocalizedLink 
              to="/transparencia"
              className="group inline-flex items-center text-white/60 hover:text-white mb-8 transition-colors duration-300"
            >
              <svg className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-sm font-medium tracking-wide">{t('transparency:backToPortal')}</span>
            </LocalizedLink>
            
            {/* Título principal */}
            <h1 className="hero-title font-baskerville font-bold text-white text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-[0.95] mb-6 drop-shadow-lg">
              <Trans i18nKey="hero.title" ns="transparencySubsidies" components={{ hl: <span className="text-intercasa-yellow" /> }} />
            </h1>
            
            {/* Subtítulo */}
            <p className="text-lg md:text-xl text-white/60 max-w-2xl font-light">
              {t('hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Contenido */}
      <section className="bg-white py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-5xl mx-auto">
            
            {/* Header de sección */}
            <AnimatedSection animation="fadeUp" className="mb-12">
              <span className="font-lato text-intercasa-red text-sm uppercase tracking-widest mb-4 block">
                {t('transparency:year2024')}
              </span>
              <h2 className="font-baskerville font-bold text-intercasa-dark text-3xl md:text-4xl lg:text-5xl">
                {t('title')}
              </h2>
            </AnimatedSection>
              
            <div className="space-y-8">
                {/* Subvención 1: Descarbonización */}
                <AnimatedSection animation="fadeUp" delay={0.1}>
                  <div className="bg-intercasa-bone/30 rounded-2xl p-8 border border-intercasa-bone hover:shadow-lg transition-shadow duration-300">
                    <div className="mb-6">
                      <h3 className="font-baskerville text-xl font-bold text-intercasa-dark mb-2">
                        {t('decarbonisation.title')}
                      </h3>
                      <p className="text-sm text-intercasa-gray leading-relaxed">
                        {t('decarbonisation.description')}
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div className="bg-white rounded-xl p-4">
                        <span className="text-xs text-intercasa-gray uppercase tracking-wider">{t('labels.amount')}</span>
                        <p className="text-2xl font-bold text-intercasa-dark mt-1">{t('decarbonisation.amount')}</p>
                      </div>
                      <div className="bg-white rounded-xl p-4">
                        <span className="text-xs text-intercasa-gray uppercase tracking-wider">{t('labels.file')}</span>
                        <p className="text-lg font-semibold text-intercasa-dark mt-1">134/1/2023-0112164902</p>
                      </div>
                      <div className="bg-white rounded-xl p-4 md:col-span-2">
                        <span className="text-xs text-intercasa-gray uppercase tracking-wider">{t('labels.purpose')}</span>
                        <p className="text-intercasa-dark/80 mt-2 leading-relaxed">
                          {t('decarbonisation.purpose')}
                        </p>
                      </div>
                      <div className="bg-white rounded-xl p-4 md:col-span-2">
                        <span className="text-xs text-intercasa-gray uppercase tracking-wider">{t('labels.authority')}</span>
                        <p className="text-intercasa-dark/80 mt-2">
                          {t('authority')}
                        </p>
                      </div>
                    </div>
                    
                    {/* Imagen de la subvención al final */}
                    <div className="bg-white rounded-xl p-4 flex justify-center">
                      <img 
                        src="/images/subvenciones/Subvencion1.webp" 
                        alt={t('decarbonisation.alt')}
                        className="max-w-full h-auto rounded-lg"
                      />
                    </div>
                  </div>
                </AnimatedSection>

                {/* Subvención 2: Autoconsumo */}
                <AnimatedSection animation="fadeUp" delay={0.2}>
                  <div className="bg-intercasa-bone/30 rounded-2xl p-8 border border-intercasa-bone hover:shadow-lg transition-shadow duration-300">
                    <div className="mb-6">
                      <h3 className="font-baskerville text-xl font-bold text-intercasa-dark mb-2">
                        {t('selfConsumption.title')}
                      </h3>
                      <p className="text-sm text-intercasa-gray leading-relaxed">
                        {t('selfConsumption.description')}
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div className="bg-white rounded-xl p-4">
                        <span className="text-xs text-intercasa-gray uppercase tracking-wider">{t('labels.amount')}</span>
                        <p className="text-2xl font-bold text-intercasa-dark mt-1">{t('selfConsumption.amount')}</p>
                      </div>
                      <div className="bg-white rounded-xl p-4">
                        <span className="text-xs text-intercasa-gray uppercase tracking-wider">{t('labels.file')}</span>
                        <p className="text-lg font-semibold text-intercasa-dark mt-1">IA2021012201</p>
                      </div>
                      <div className="bg-white rounded-xl p-4 md:col-span-2">
                        <span className="text-xs text-intercasa-gray uppercase tracking-wider">{t('labels.purpose')}</span>
                        <p className="text-intercasa-dark/80 mt-2 leading-relaxed">
                          {t('selfConsumption.purpose')}
                        </p>
                      </div>
                      <div className="bg-white rounded-xl p-4 md:col-span-2">
                        <span className="text-xs text-intercasa-gray uppercase tracking-wider">{t('labels.authority')}</span>
                        <p className="text-intercasa-dark/80 mt-2">
                          {t('authority')}
                        </p>
                      </div>
                    </div>
                    
                    {/* Imagen de la subvención al final */}
                    <div className="bg-white rounded-xl p-4 flex justify-center">
                      <img 
                        src="/images/subvenciones/Subvencion2.webp" 
                        alt={t('selfConsumption.alt')}
                        className="max-w-full h-auto rounded-lg"
                      />
                    </div>
                  </div>
                </AnimatedSection>

                {/* Subvención 3: Imagen adicional */}
                <AnimatedSection animation="fadeUp" delay={0.25}>
                  <div className="bg-intercasa-bone/30 rounded-2xl p-8 border border-intercasa-bone hover:shadow-lg transition-shadow duration-300">
                    <h3 className="font-baskerville text-xl font-bold text-intercasa-dark mb-6">
                      {t('transport.title')}
                    </h3>
                    <div className="bg-white rounded-xl p-4 flex justify-center mb-4">
                      <img
                        src="/images/subvenciones/Subvencion3.webp"
                        alt={t('transport.alt')}
                        className="max-w-full h-auto rounded-lg"
                      />
                    </div>
                    <div className="flex flex-wrap gap-4 justify-center">
                      <a
                        href="/images/subvenciones/PDF/SubvencionesMaritimo.pdf"
                        download
                        className="inline-flex items-center px-6 py-3 bg-intercasa-red text-white rounded-lg hover:bg-intercasa-red/90 transition-colors duration-300 font-lato font-medium"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {t('transparency:download.pdf')}
                      </a>
                      <a
                        href="/images/subvenciones/PDF/SubvencionesMaritimo.docx"
                        download
                        className="inline-flex items-center px-6 py-3 bg-intercasa-dark text-white rounded-lg hover:bg-intercasa-dark/90 transition-colors duration-300 font-lato font-medium"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {t('transparency:download.docx')}
                      </a>
                    </div>
                  </div>
                </AnimatedSection>

                {/* Subvención 5: Autoconsumo Fotovoltaico en Cubierta */}
                <AnimatedSection animation="fadeUp" delay={0.29}>
                  <div className="bg-intercasa-bone/30 rounded-2xl p-8 border border-intercasa-bone hover:shadow-lg transition-shadow duration-300">
                    <h3 className="font-baskerville text-xl font-bold text-intercasa-dark mb-6">
                      {t('rooftop.title')}
                    </h3>
                    <div className="bg-white rounded-xl p-4 flex justify-center mb-4">
                      <img
                        src="/images/subvenciones/Cartel Intercas Autoconsumo.jpg"
                        alt={t('rooftop.alt')}
                        className="max-w-full h-auto rounded-lg"
                      />
                    </div>
                    <div className="flex flex-wrap gap-4 justify-center">
                      <a
                        href="/images/subvenciones/PDF/Cartel Intercas Autoconsumo.pdf"
                        download
                        className="inline-flex items-center px-6 py-3 bg-intercasa-red text-white rounded-lg hover:bg-intercasa-red/90 transition-colors duration-300 font-lato font-medium"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {t('transparency:download.pdf')}
                      </a>
                      <a
                        href="/images/subvenciones/PDF/Autoconsumo.odt"
                        download
                        className="inline-flex items-center px-6 py-3 bg-intercasa-dark text-white rounded-lg hover:bg-intercasa-dark/90 transition-colors duration-300 font-lato font-medium"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {t('transparency:download.odt')}
                      </a>
                    </div>
                  </div>
                </AnimatedSection>

                {/* Subvención 6: Prórroga - Descarbonización Industrial */}
                <AnimatedSection animation="fadeUp" delay={0.31}>
                  <div className="bg-intercasa-bone/30 rounded-2xl p-8 border border-intercasa-bone hover:shadow-lg transition-shadow duration-300">
                    <h3 className="font-baskerville text-xl font-bold text-intercasa-dark mb-6">
                      {t('extension.title')}
                    </h3>
                    <div className="bg-white rounded-xl p-4 flex justify-center mb-4">
                      <img
                        src="/images/subvenciones/Cartel Intercasa Prórroga.jpg"
                        alt={t('extension.alt')}
                        className="max-w-full h-auto rounded-lg"
                      />
                    </div>
                    <div className="flex flex-wrap gap-4 justify-center">
                      <a
                        href="/images/subvenciones/PDF/Cartel Intercasa Prórroga.pdf"
                        download
                        className="inline-flex items-center px-6 py-3 bg-intercasa-red text-white rounded-lg hover:bg-intercasa-red/90 transition-colors duration-300 font-lato font-medium"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {t('transparency:download.pdf')}
                      </a>
                      <a
                        href="/images/subvenciones/PDF/Prorroga.docx"
                        download
                        className="inline-flex items-center px-6 py-3 bg-intercasa-dark text-white rounded-lg hover:bg-intercasa-dark/90 transition-colors duration-300 font-lato font-medium"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {t('transparency:download.docx')}
                      </a>
                    </div>
                  </div>
                </AnimatedSection>

                {/* Resumen Total */}
                <AnimatedSection animation="fadeUp" delay={0.3}>
                  <div className="text-center mt-12">
                    <div className="inline-flex items-center px-8 py-4 bg-intercasa-dark rounded-full">
                      <span className="text-white font-bold">{t('total.label')}</span>
                      <span className="text-intercasa-yellow font-bold ml-3 text-2xl">{t('total.amount')}</span>
                    </div>
                    <p className="text-center text-sm text-intercasa-gray mt-4">
                      {t('financedByEu')}
                    </p>
                  </div>
                </AnimatedSection>

                {/* Botones de descarga */}
                <AnimatedSection animation="fadeUp" delay={0.4}>
                  <div className="flex flex-wrap gap-4 justify-center mt-12">
                    <a 
                      href="/PortalTansparenciaIntercasa/AyudaySubvencion/AyudasySubvencionesIntercasa.pdf" 
                      download
                      className="inline-flex items-center px-6 py-3 bg-intercasa-red text-white rounded-lg hover:bg-intercasa-red/90 transition-colors duration-300 font-lato font-medium"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {t('transparency:download.pdf')}
                    </a>
                    <a
                      href="/PortalTansparenciaIntercasa/AyudaySubvencion/AyudasySubvencionesIntercasa.odt"
                      download
                      className="inline-flex items-center px-6 py-3 bg-intercasa-dark text-white rounded-lg hover:bg-intercasa-dark/90 transition-colors duration-300 font-lato font-medium"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {t('transparency:download.odt')}
                    </a>
                  </div>
                </AnimatedSection>
              </div>
          </div>
        </div>
      </section>
    </ImmersiveLayout>
  );
};

export default SubsidiesInfoPage;